import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Form } from "semantic-ui-react";
import EditEntityPopup from '../../../components/shared/EditEntityPopup';
import { useMessageResources, useTextResources } from '../../../language';
import { useCallback } from "react";
import useBoolean from "../../../hooks/useBoolean";
import useSafeState from "../../../hooks/useSafeState";
import TextInput from "../../../components/shared/controlled/TextInput";
import TaxIdentityHotelsTransfer from "../components/TaxIdentityHotelsTransfer";
import NextUsersDropDown from "../../../components/shared/NextUsersDropDown";
import { useCurrentHotel } from "../../../hooks/useCurrentHotel";
import { onWarning, onError } from '../../../components/shared/messages';
import { TaxIdentityFeatureEnum } from '../../../components/shared/constants';
import TaxIdentityTypeDropDown from '../components/TaxIdentityTypeDropDown';
import { hasDigitalCertificate }from '../../../components/shared/utils';

const defaultState = {
    Id: 0,
    Name: '',
    UserId: undefined,
    IsActive: true,
    TaxIdentityHotels: [],
    Type: TaxIdentityFeatureEnum.TaxAuthority
};

const usersDropDownSpecDefault = {
    IsActive: true,
    HasEmail: true,
    // IsAdmin: false,
    HaveDigitalCertificateFeature: false,
};

const EditTaxIdentityPopup = ({
    open,
    taxIdentity,
    save,
    onClose,
}) => {
    const text = useTextResources();
    const message = useMessageResources();
    const currentHotel = useCurrentHotel();
    const isCreate = !taxIdentity;
    const usersDropDownSpecStart = { ...usersDropDownSpecDefault };
    usersDropDownSpecStart.HaveDigitalCertificateFeature = taxIdentity?.Type == TaxIdentityFeatureEnum.DigitalInvoice;
    defaultState.Type = taxIdentity?.Type ?? TaxIdentityFeatureEnum.TaxAuthority;

    const {
        value: isLoading,
        setTrue: setLoading,
        setFalse: resetLoading
    } = useBoolean(false);
    const [taxIdentityState, setTaxIdentityState] = useSafeState(defaultState);
    const [usersDropDownSpec, setUsersDropDownSpec] = useState(usersDropDownSpecStart);

    const resetState = useCallback(() => {
        setTaxIdentityState({
            HotelChainId: currentHotel.HotelChainId,
            ClientId: crypto.randomUUID(),
            ...defaultState,
            ...taxIdentity,
        });
    }, [currentHotel.HotelChainId, taxIdentity, setTaxIdentityState]);

    useEffect(() => {
        resetState();
    }, [resetState]);

    const setName = useCallback((value) => {
        setTaxIdentityState((prevState) => ({
            ...prevState,
            Name: value,
        }));
    }, [setTaxIdentityState]);

    const setType = useCallback((value) => {
        setTaxIdentityState((prevState) => ({
            ...prevState,
            Type: value,
        }));
        setUsersDropDownSpec((prevState) => ({
            ...prevState,
            HaveDigitalCertificateFeature: value == TaxIdentityFeatureEnum.DigitalInvoice,
        }));
    }, [setTaxIdentityState]);

    const setUserId = useCallback((value) => {
        setTaxIdentityState((prevState) => ({
            ...prevState,
            UserId: value ? value : null,
        }));
    }, [setTaxIdentityState]);

    const setTaxIdentityHotels = useCallback((value) => {
        setTaxIdentityState((prevState) => ({
            ...prevState,
            TaxIdentityHotels: value,
        }));
    }, [setTaxIdentityState]);

    const isValid = useCallback(async (taxIdentityState) => {
        try {
            if (!taxIdentityState.Name || !taxIdentityState.TaxIdentityHotels.length) {
                onWarning(message("AllFieldsAreRequired"));
                return false;
            }
            if (taxIdentityState.Type === TaxIdentityFeatureEnum.DigitalInvoice) {
                if (!(await hasDigitalCertificate(taxIdentityState.UserId))) {
                    onWarning(message("digitalSignatureCertificateRequired"));
                    return false;
                }
            }
            return true;
        } catch (error) {
            onError(error);
            return false;
        }
    }, [message]);

    const handleSave = useCallback(async () => {
        if (!(await isValid(taxIdentityState))) {
            return false;
        }

        try {
            setLoading();
            await save(taxIdentityState);
        } catch {
            return false;
        } finally {
            resetLoading();
        }

        return true;
    }, [taxIdentityState, isValid, resetLoading, save, setLoading]);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    const handleSaveAndCreateNew = useCallback(async () => {
        const isSaved = await handleSave();
        isSaved && resetState();
        return isSaved;
    }, [handleSave, resetState]);

    const handleSaveAndClose = useCallback(async () => {
        const isSaved = await handleSave();
        isSaved && handleClose();
    }, [handleSave, handleClose]);

    return (
        <EditEntityPopup
            open={open}
            isCreate={isCreate}
            header={`${isCreate ? text("Create") : text("Edit")} ${text("ExternalFiscalInvoices")}`}
            onClose={handleClose}
            onSave={isCreate ? handleSaveAndCreateNew : undefined}
            onSaveAndClose={handleSaveAndClose}
            isLoading={isLoading}
        >
            <Form>
                <Form.Field
                    label={text("Name")}
                    control={TextInput}
                    onBlur={setName}
                    value={taxIdentityState.Name}
                    placeholder={text("Name")}
                    maxLength={100}
                />
                <Form.Field
                    label={text("Feature")}
                    control={TaxIdentityTypeDropDown}
                    onChange={setType}
                    value={taxIdentityState.Type}
                    placeholder={text("Feature")}
                    disabled={!isCreate}
                />
                <Form.Field
                    label={text("User")}
                    control={NextUsersDropDown}
                    spec={usersDropDownSpec}
                    onChange={setUserId}
                    value={taxIdentityState.UserId}
                    placeholder={text("User")}
                    search
                    clearable
                />,
                <Form.Field
                    label={text("TaxIdentityHotels")}
                    control={TaxIdentityHotelsTransfer}
                    taxIdentityId={taxIdentityState.Id}
                    taxIdentityType={taxIdentityState.Type}
                    taxIdentityClientId={taxIdentityState.ClientId}
                    onChange={setTaxIdentityHotels}
                    value={taxIdentityState.TaxIdentityHotels}
                />
            </Form>
        </EditEntityPopup>
    );
};

EditTaxIdentityPopup.propTypes = {
    open: PropTypes.bool.isRequired,
    taxIdentity: PropTypes.shape({
        Id: PropTypes.number.isRequired,
        Name: PropTypes.string.isRequired,
        Type: PropTypes.number.isRequired,
        TaxIdentityHotels: PropTypes.arrayOf(PropTypes.shape({
            Id: PropTypes.number.isRequired,
            TaxIdentityId: PropTypes.number.isRequired,
            HotelId: PropTypes.number.isRequired,
        })),
    }),
    onClose: PropTypes.func.isRequired,
    save: PropTypes.func,
};

EditTaxIdentityPopup.defaultProps = {
    open: false,
    taxIdentity: undefined,
    onClose: () => { },
    save: () => Promise.resolve(),
}

export default EditTaxIdentityPopup;